import {useEffect, useRef, useState} from "react";
import {BackgroundTask, BackgroundTaskId, CompleteBackgroundTask, PendingBackgroundTask} from "platformed-client/types";
import {PromiseStateReturnType, usePromiseState} from "./promiseState";
import {useSubscription, useSubscriptions} from "./pusher";
import {PlatformedQueryFilters, invalidateQueries, q, setQueryData, useQueriesData} from "../state";
import {toaster, ToastOptions} from "../components/ui/toaster";

// Hook to receive live updates about a resource which an ongoing background task is updating
export function useBackgroundTask(
  // The background task which is updating the resource
  backgroundTask: PendingBackgroundTask | undefined | null,
  // A query filter to invalidate when the task progresses
  filters: PlatformedQueryFilters[],
  // A query filter to invalidate when the task completes
  completionFilters?: PlatformedQueryFilters[],
) {
  const wasRunning = useRef(false);
  const isRunning = !!backgroundTask;
  useEffect(() => {
    if (!isRunning && wasRunning.current) {
      invalidateQueries(completionFilters);
    }
    wasRunning.current = isRunning;
  }, [isRunning, completionFilters]);
  useSubscription(backgroundTask?.channel_name, null, eventName => {
    switch (eventName) {
      case "pusher:subscription_error":
        break;
      case "pusher:subscription_succeeded":
      case "progress":
        invalidateQueries(filters);
        break;
      case "complete":
        invalidateQueries([...filters, ...(completionFilters ?? [])]);
        break;
      default:
        console.error("Unhandled event", eventName);
    }
  });
}

export function useBackgroundTasks(
  backgroundTasks: PendingBackgroundTask[],
  // Query filters to invalidate when the task progresses
  progressFilters: PlatformedQueryFilters[],
  // A query filter to invalidate when the task completes
  completionFilters?: PlatformedQueryFilters[],
) {
  const wasRunning = useRef(false);
  const isRunning = backgroundTasks.length > 0;
  useEffect(() => {
    if (!isRunning && wasRunning.current) {
      invalidateQueries(completionFilters);
    }
    wasRunning.current = isRunning;
  }, [isRunning, completionFilters]);

  useSubscriptions(
    (Array.isArray(backgroundTasks) ? backgroundTasks : []).map(task => task.channel_name).filter(x => x),
    null,
    eventName => {
      switch (eventName) {
        case "pusher:subscription_error":
          break;
        case "pusher:subscription_succeeded":
        case "progress":
          invalidateQueries(progressFilters);
          break;
        case "complete":
          invalidateQueries([...progressFilters, ...(completionFilters ?? [])]);
          break;
        default:
          console.error("Unhandled event", eventName);
      }
    },
  );
}

// Similar to `useBackgroundTask` but for when the background task is not tied to
// any particular resource. For customer-facing UI we should always aim to tie
// a background task to a resource, so that refreshing the page doesn't cause us
// to "forget" that a task is in progress. For that reason, this hook should
// only be used on internal (Platformed-staff) facing UI.
export function useBackgroundTaskState<P extends unknown[]>(
  // The callback which returns a newly created background task
  f: (...args: P) => Promise<PendingBackgroundTask>,
  // Dependency list for the callback
  deps: React.DependencyList,
  onComplete?: {cb?: (completeTask?: CompleteBackgroundTask) => void; toastOptions?: ToastOptions},
): [
  {
    /** Is either the asynchronous callback or the background task pending? */
    inProgress: boolean;
    /** Current background task state. */
    data: BackgroundTask | null;
    /** Last error. */
    error?: unknown;
  },
  (...args: P) => Promise<PromiseStateReturnType<PendingBackgroundTask>>,
] {
  const [taskId, setTaskId] = useState<BackgroundTaskId | null>(null);
  const [state, cb] = usePromiseState(async (...args: P) => {
    const task = await f(...args);
    setTaskId(task.background_task_id);
    setQueryData(q.backgroundTask(task.background_task_id), {type: "Pending", content: task});
    return task;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const [task] = useQueriesData({queries: taskId ? [q.backgroundTask(taskId)] : []});

  useBackgroundTask(
    task?.type === "Pending" ? task?.content : undefined,
    taskId ? [q.backgroundTask.filter(taskId)] : [],
  );

  useEffect(() => {
    if (taskId && (!task || task?.type === "Complete")) {
      onComplete?.toastOptions && toaster.create(onComplete.toastOptions);
      setTaskId(null);
      onComplete?.cb && onComplete?.cb(task?.content);
    }
  }, [taskId, task, onComplete, onComplete?.toastOptions, onComplete?.cb]);

  return [
    {
      inProgress: state.inProgress || (task != null && task.type === "Pending"),
      error: state.lastError,
      data: task ?? null,
    },
    cb,
  ];
}
